/**
 * A SID voice described as data (05 §1): what one note of one oscillator does. Every sound
 * effect and every note of the music is a function that returns some of these, so all of it can
 * be checked in Node; `sid.ts` turns them into WebAudio nodes. Pure.
 */

/** The SID's four waveforms. */
export type Wave = 'triangle' | 'sawtooth' | 'pulse' | 'noise';

/** Seconds, and a 0..1 sustain level. Decay and release fall exponentially, as the chip's did. */
export interface Adsr {
  readonly a: number;
  readonly d: number;
  readonly s: number;
  readonly r: number;
}

export interface FilterSpec {
  readonly type: 'lowpass' | 'highpass' | 'bandpass';
  /** Cutoff at the start, Hz. */
  readonly freq: number;
  /** Cutoff at the end of the sweep, Hz (exponential). */
  readonly to?: number;
  /** Seconds the sweep takes; defaults to the whole voice. */
  readonly sweep?: number;
  /** WebAudio's Q: resonance in dB for low/high-pass, bandwidth for band-pass. */
  readonly q: number;
}

export interface VoiceSpec {
  readonly wave: Wave;
  /** Seconds after the sound starts. */
  readonly at: number;
  /** Seconds the gate is held; the release follows. */
  readonly gate: number;
  /** Hz. For noise, the rate the noise generator changes value: the SID's noise "pitch". */
  readonly freq: number;
  /** Glide to this frequency (exponential) over `glide` seconds, default the gate. */
  readonly to?: number;
  readonly glide?: number;
  /** Frequencies in turn, one every `stepTime` seconds: the SID's frame-rate arpeggio. */
  readonly steps?: readonly number[];
  /** Default 0.02 s: the C64's 50 Hz frame. */
  readonly stepTime?: number;
  /** Play the steps once and hold the last, rather than cycling them. */
  readonly once?: boolean;
  /** Pulse width 0..1 (pulse only; 0.5 is square). */
  readonly width?: number;
  /** Pulse-width modulation: the width swings +-`depth` at `rate` Hz (pulse only). */
  readonly pwm?: { readonly rate: number; readonly depth: number };
  /** Vibrato: `depth` cents at `rate` Hz, fading in after `delay` seconds. */
  readonly vibrato?: { readonly rate: number; readonly depth: number; readonly delay: number };
  readonly env: Adsr;
  /** Peak level 0..1 before the bus. */
  readonly level: number;
  readonly filter?: FilterSpec;
}

/** The C64 PAL frame: arpeggios and pitch steps move at 50 Hz. */
export const FRAME = 0.02;

export function adsr(a: number, d: number, s: number, r: number): Adsr {
  return { a, d, s, r };
}

/** Seconds from the voice's start until it is silent. */
export function voiceLength(v: VoiceSpec): number {
  return v.at + v.gate + v.env.r;
}

/** The longest of a sound's voices. */
export function soundLength(voices: readonly VoiceSpec[]): number {
  return voices.reduce((m, v) => Math.max(m, voiceLength(v)), 0);
}

/** The same voice shifted later by `dt` seconds. */
export function delayed(v: VoiceSpec, dt: number): VoiceSpec {
  return { ...v, at: v.at + dt };
}

const okHz = (hz: number): boolean => Number.isFinite(hz) && hz >= 10 && hz <= 24000;
const okTime = (t: number): boolean => Number.isFinite(t) && t >= 0 && t <= 30;

/** Why a voice cannot be played, or null if it is sound. For the tests and the debug console. */
export function checkVoice(v: VoiceSpec): string | null {
  if (!okHz(v.freq)) return `freq ${v.freq}`;
  if (v.to !== undefined && !okHz(v.to)) return `glide to ${v.to}`;
  if (!okTime(v.at) || !okTime(v.gate) || v.gate <= 0) return `timing at ${v.at} gate ${v.gate}`;
  if (v.glide !== undefined && !(v.glide > 0)) return `glide ${v.glide}`;
  const e = v.env;
  if (![e.a, e.d, e.r].every(okTime) || !(e.s >= 0 && e.s <= 1)) return 'envelope';
  if (!(v.level > 0 && v.level <= 1)) return `level ${v.level}`;
  if (v.steps !== undefined && (v.steps.length === 0 || !v.steps.every(okHz))) return 'steps';
  if (v.stepTime !== undefined && !(v.stepTime >= 0.005)) return `stepTime ${v.stepTime}`;
  if (v.width !== undefined && !(v.width > 0 && v.width < 1)) return `width ${v.width}`;
  if (v.pwm && !(v.pwm.rate > 0 && v.pwm.depth >= 0 && v.pwm.depth < 0.5)) return 'pwm';
  if (v.vibrato && !(v.vibrato.rate > 0 && v.vibrato.depth >= 0 && okTime(v.vibrato.delay))) return 'vibrato';
  const f = v.filter;
  if (f && (!okHz(f.freq) || (f.to !== undefined && !okHz(f.to)) || !Number.isFinite(f.q))) return 'filter';
  if (f?.sweep !== undefined && !(f.sweep > 0)) return 'filter sweep';
  return null;
}
